import React from "react";
import { Button } from "./ui/button";
import { ArrowRight, GraduationCap, Handshake, Sparkles, PlayCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { colors, images, educationProgram } from "../config/site";

const Hero = () => {
  return (
    <section
      id="home"
      className="pt-28 pb-16 px-4"
      style={{ backgroundColor: colors.cream }}
      data-testid="hero-section"
    >
      <div className="container mx-auto max-w-7xl">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          {/* Left Content */}
          <div className="lg:col-span-7 space-y-7">
            <div
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider"
              style={{ backgroundColor: "#EFE6D6", color: colors.terracottaDark }}
            >
              <GraduationCap className="w-3.5 h-3.5" />
              Education-First Affordable Housing
            </div>

            <h1
              className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-[1.1] tracking-tight"
              style={{ color: colors.charcoal }}
            >
              Housing stability creates{" "}
              <span style={{ color: colors.primary }}>successful households</span>.
            </h1>

            <p
              className="text-lg lg:text-xl leading-relaxed max-w-2xl"
              style={{ color: colors.textMuted }}
            >
              Workshops, guides, and tools that help families and individuals stay housed, build credit, and grow toward homeownership.
            </p>

            <p
              className="text-sm lg:text-base italic max-w-2xl pl-4 border-l-2"
              style={{ color: colors.textMuted, borderColor: colors.terracotta }}
            >
              Built by a former school behavioral therapist who knows: stable homes raise successful students, parents, and communities.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <Link to="/quiz/rent-readiness" data-testid="hero-cta-quiz">
                <Button
                  className="text-white px-7 py-6 rounded-full font-semibold text-base shadow-md hover:shadow-xl transition-all w-full sm:w-auto"
                  style={{ backgroundColor: colors.primary }}
                >
                  <Sparkles className="mr-2 w-4 h-4" />
                  Take the Rent Readiness Quiz
                </Button>
              </Link>

              <Link to="/resources" data-testid="hero-cta-resources">
                <Button
                  className="px-7 py-6 rounded-full font-semibold text-base shadow-md hover:shadow-xl transition-all w-full sm:w-auto text-white"
                  style={{ backgroundColor: colors.terracotta }}
                >
                  Browse Resources
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>

            <div
              className="flex flex-wrap items-center gap-x-7 gap-y-2 pt-3 text-sm"
              style={{ color: colors.textMuted }}
            >
              <div className="flex items-center gap-2">
                <span
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: colors.sage }}
                ></span>
                Open to All Households
              </div>
              <div className="flex items-center gap-2">
                <span
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: colors.terracotta }}
                ></span>
                Workshops for Partner Orgs
              </div>
              <div className="flex items-center gap-2">
                <span
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: colors.gold }}
                ></span>
                Women-Led &amp; Community-Rooted
              </div>
            </div>
          </div>

          {/* Right Content - Image (video-ready placeholder) */}
          <div className="lg:col-span-5 relative">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl">
              <img
                src={images.heroFamily}
                alt="A family at home — what stable housing makes possible"
                className="w-full h-[460px] lg:h-[520px] object-cover"
              />
              <div
                className="absolute inset-0"
                style={{
                  background: `linear-gradient(180deg, ${colors.primaryDark}40 0%, ${colors.primaryDark}55 55%, ${colors.primaryDark}e6 100%)`,
                }}
              />
              {/* "Watch a Workshop" preview overlay — wires to YouTube when ready */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div
                  className="flex items-center gap-2 px-5 py-3 rounded-full backdrop-blur-md bg-white/15 border border-white/30 text-white text-sm font-semibold"
                  data-testid="hero-video-placeholder"
                >
                  <PlayCircle className="w-5 h-5" />
                  Workshop preview coming soon
                </div>
              </div>
              <div className="absolute bottom-6 left-6 right-6 text-white">
                <p
                  className="text-xs uppercase tracking-wider opacity-90 mb-1"
                  style={{ color: colors.gold }}
                >
                  {educationProgram.tagline}
                </p>
                <p className="text-lg font-semibold">
                  Education. Stability. A path forward.
                </p>
              </div>
            </div>

            <div
              className="hidden md:flex absolute -bottom-6 -left-6 items-center gap-3 px-5 py-4 rounded-2xl shadow-xl"
              style={{ backgroundColor: colors.white }}
            >
              <div
                className="w-11 h-11 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: "#EFE6D6", color: colors.terracottaDark }}
              >
                <Handshake className="w-5 h-5" />
              </div>
              <div>
                <p
                  className="text-xs uppercase tracking-wider font-semibold"
                  style={{ color: colors.sage }}
                >
                  Two Missions
                </p>
                <p className="text-sm font-bold" style={{ color: colors.charcoal }}>
                  Educate + Develop
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
